import { FC, useState, useEffect, useCallback, useRef } from "react";
import { Box, styled, Typography } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import { black, background } from "@/theme/colors";
import { useZoomPan } from "@/features/talent/components/talent-scan-image/use-zoom-pan";
import { ZoomControls } from "@/features/talent/components/talent-scan-image/ZoomControls";

type TPdfPreviewWithZoomProps = {
  src: string | File;
  width?: number | string;
  height?: number;
};

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";

// Render scale per page — using separate canvases avoids browser canvas size limits
const RENDER_SCALE = 3;
// Gap between pages in base pixels
const PAGE_GAP = 8;

type TPageCanvas = {
  canvas: HTMLCanvasElement;
  baseWidth: number;
  baseHeight: number;
};

export const PdfPreviewWithZoom: FC<TPdfPreviewWithZoomProps> = ({ src, width = "100%", height = 500 }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  // Total content dimensions (all pages stacked) at scale=1
  const [contentDimensions, setContentDimensions] = useState({ width: 0, height: 0 });
  const [pageCanvases, setPageCanvases] = useState<TPageCanvas[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  const { scale, position, isDragging, zoomIn, zoomOut, fitToViewport, handlers } = useZoomPan({
    contentWidth: contentDimensions.width,
    contentHeight: contentDimensions.height,
    containerRef,
    fitOnLoad: true,
  });

  // Load PDF and render ALL pages, each on its own offscreen canvas
  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | null = null;

    const loadAndRenderPdf = async () => {
      if (!src) return;

      setIsLoading(true);

      let url: string;
      if (typeof src === "string") {
        url = src;
      } else if (src instanceof File) {
        objectUrl = URL.createObjectURL(src);
        url = objectUrl;
      } else {
        return;
      }

      setPdfUrl(url);

      try {
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;
        if (isCancelled) return;

        const numPages = pdf.numPages;
        const rendered: TPageCanvas[] = [];
        let maxBaseWidth = 0;
        let totalBaseHeight = 0;

        for (let i = 1; i <= numPages; i++) {
          const page = await pdf.getPage(i);
          if (isCancelled) return;

          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const offscreen = document.createElement("canvas");
          offscreen.width = viewport.width;
          offscreen.height = viewport.height;

          const ctx = offscreen.getContext("2d");
          if (!ctx) continue;

          await page.render({ canvasContext: ctx, viewport }).promise;
          if (isCancelled) return;

          const baseW = viewport.width / RENDER_SCALE;
          const baseH = viewport.height / RENDER_SCALE;
          rendered.push({ canvas: offscreen, baseWidth: baseW, baseHeight: baseH });
          maxBaseWidth = Math.max(maxBaseWidth, baseW);
          totalBaseHeight += baseH;
        }

        // Add gaps between pages
        totalBaseHeight += (numPages - 1) * PAGE_GAP;

        if (isCancelled) return;

        setPageCanvases(rendered);
        setContentDimensions({ width: maxBaseWidth, height: totalBaseHeight });
        setIsLoading(false);
      } catch (error) {
        console.error("PDF render failed:", error);
        setIsLoading(false);
      }
    };

    loadAndRenderPdf();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  // Copy offscreen canvas data to visible canvas ref
  const attachCanvas = useCallback((el: HTMLCanvasElement | null, pageData: TPageCanvas) => {
    if (!el || el.dataset.rendered === "true") return;
    el.width = pageData.canvas.width;
    el.height = pageData.canvas.height;
    const ctx = el.getContext("2d");
    if (ctx) ctx.drawImage(pageData.canvas, 0, 0);
    el.dataset.rendered = "true";
  }, []);

  if (!pdfUrl && !isLoading) {
    return <PreviewContainer width={width} height={height} />;
  }

  return (
    <PreviewContainer width={width} height={height}>
      <PdfViewport ref={containerRef} height={height - 44} isDragging={isDragging} {...handlers}>
        {isLoading && (
          <LoadingOverlay>
            <Typography variant="cap12" color={black[70]}>
              読み込み中...
            </Typography>
          </LoadingOverlay>
        )}
        <PagesContainer
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "0 0",
            opacity: isLoading ? 0 : 1,
            width: contentDimensions.width,
          }}
        >
          {pageCanvases.map((pageData, index) => (
            <StyledCanvas
              key={index}
              ref={(el) => attachCanvas(el, pageData)}
              style={{
                width: pageData.baseWidth,
                height: pageData.baseHeight,
                marginBottom: index < pageCanvases.length - 1 ? PAGE_GAP : 0,
              }}
            />
          ))}
        </PagesContainer>
      </PdfViewport>
      <ZoomControls scale={scale} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={fitToViewport} />
    </PreviewContainer>
  );
};

const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "height",
})<{ width: number | string; height: number }>`
  width: ${({ width }) => (typeof width === "number" ? `${width}px` : width)};
  height: ${({ height }) => height}px;
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  margin-bottom: 4px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${background.white};
  border: 1px solid ${black[95]};
`;

const PdfViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "isDragging",
})<{ height: number; isDragging: boolean }>`
  flex: 1;
  overflow: hidden;
  height: ${({ height }) => height}px;
  cursor: ${({ isDragging }) => (isDragging ? "grabbing" : "grab")};
  position: relative;
  background-color: ${background.disable};
`;

const PagesContainer = styled(Box)`
  position: absolute;
  will-change: transform;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledCanvas = styled("canvas")`
  display: block;
  user-select: none;
`;

const LoadingOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${background.disable};
`;
